import React from "react";

import { Flex } from "../Flex";
import { Spacer } from "../Grid/Spacer";
import { Text } from "../Text";

import { Rate } from "./Rate";
import styles from "./StarRating.scss";

/**
 * Props for the StarRating component.
 */
export interface StarRatingProps {
  /**
   * Scale that will initiate the rating count
   * @default 0
   */
  scaleStart?: number;
  /**
   * Scale that will end the rating count
   * @default 5
   */
  scaleEnd?: number;
  /**
   * StarRating value
   * @default 0
   */
  rating?: number;
  /**
   * Scale value. One of: 0.25|0.5|1
   * @default 1
   */
  scale?: 0.25 | 0.5 | 1;
  /**
   * Text to display beneath the start scale
   * @default ""
   */
  startLabel?: string;
  /**
   * Text to display beneath the end scale
   * @default ""
   */
  endLabel?: string;
  /**
   * Function to call when a new rating is selected
   */
  onChange: (newRating: number) => void;
  /**
   * Displays an error message
   */
  error?: string;
  /**
   * Disables onClick action and can't set new values
   * @default false
   */
  disabled?: boolean;
}

/**
 * An accessible rating component.
 *
 * @example
 * <StarRating rating={3.5} onChange={(newRating) => console.log(newRating)} />
 */
export function StarRating({
  rating = 0,
  endLabel = "",
  onChange,
  scale = 1,
  scaleEnd = 5,
  scaleStart = 0,
  startLabel = "",
  disabled,
  error,
}: StarRatingProps): JSX.Element {
  const [hoverRating, setHoverRating] = React.useState(0);

  const onHandleMouseMove = React.useCallback(
    // istanbul ignore next - deals with screens and pixels in ways our unit tests aren't constructed to test
    (e: React.MouseEvent<HTMLButtonElement>, currentHovering: number): void => {
      // One day I'll stumble upon a better way to do this...
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const element = e.target as HTMLElement | null;

      if (!element) {
        return;
      }

      const { x: elX, width: elWidth } = element.getBoundingClientRect();
      const cursorX = e.clientX;
      const chunkWidth = elWidth * scale;

      let accumulativeWidth = elX;
      let accumulativeValue = currentHovering - 1;

      while (accumulativeWidth < elX + elWidth) {
        const accumulativeWidthPlusAChunk = accumulativeWidth + chunkWidth;
        const x1 = cursorX >= accumulativeWidth;
        const x2 = cursorX < accumulativeWidthPlusAChunk;

        if (x1 && x2) {
          setHoverRating(accumulativeValue + scale);
          break;
        }

        accumulativeWidth += chunkWidth;
        accumulativeValue += scale;
      }
    },
    [setHoverRating, scale]
  );

  const onHandleMouseLeave = React.useCallback(() => {
    setHoverRating(0);
  }, [setHoverRating]);

  const onHandleClick = React.useCallback(
    (value: number) => {
      const val = hoverRating || value;

      if (val === rating) {
        onChange(0);
        return;
      }

      onChange(val);
    },
    [hoverRating, rating]
  );

  const calculateNumberOfRates = React.useCallback((): number[] => {
    const numberOfRates = scaleEnd - scaleStart;
    return Array.from(new Array(numberOfRates)).map(
      (_, index) => index + 1 + scaleStart
    );
  }, [scaleEnd, scaleStart]);
  const rates = React.useMemo(calculateNumberOfRates, [scaleEnd, scaleStart]);

  const renderLabel = React.useCallback(
    (shouldRender: boolean, label?: string): JSX.Element | null => {
      if (!shouldRender || !label) {
        return null;
      }

      return (
        <div className={styles.label}>
          <Spacer size="small" />
          {label}
        </div>
      );
    },
    []
  );

  const hasError = error ? error.trim() !== "" : false;

  return (
    <div className={styles.container}>
      <HelperText content={error} classNameString="group-helper-text--error" />
      <Flex justifyContent="space-between" alignItems="flex-start">
        {rates.map((value, i) => (
          <Flex direction="column" className={styles["rate-container"]} key={i}>
            <Rate
              value={value}
              currentRating={hoverRating || rating}
              onMouseMove={onHandleMouseMove}
              onMouseLeave={onHandleMouseLeave}
              onClick={() => onHandleClick(value)}
              hasError={hasError}
              disabled={disabled}
            />
            {renderLabel(i === 0, startLabel)}
            {renderLabel(i === rates.length - 1, endLabel)}
          </Flex>
        ))}
      </Flex>
    </div>
  );
}

interface HelperTextProps {
  content?: string;
  classNameString: string;
}

function HelperText({
  content,
  classNameString,
}: HelperTextProps): JSX.Element | null {
  if (!content) {
    return null;
  }

  return (
    <Text
      size="paragraphXsmall"
      paragraph
      className={styles[classNameString]}
      data-testid="error-message"
    >
      {content}
    </Text>
  );
}
